import { Link } from 'react-router-dom';
import usePageMeta from '../../hooks/usePageMeta';
import { ArrowRight, Briefcase, BookOpen, Users } from 'lucide-react';

const mainServices = [
  {
    id: 'consulting',
    title: 'Strategic Consulting',
    description: 'Expert strategic and operational consulting designed to help long-term care organizations achieve sustainable growth, operational efficiency, and strategic clarity.',
    icon: Briefcase,
    link: '/services/consulting',
    color: 'primary',
  },
  {
    id: 'training',
    title: 'Training Services',
    description: 'Build organizational capacity and advance your team\'s expertise with our flexible, comprehensive training programs and learning management system.',
    icon: BookOpen,
    link: '/services/training',
    color: 'blue',
  },
  {
    id: 'staffing',
    title: 'Staffing Services',
    description: 'Access vetted professionals and flexible staffing solutions designed to fill critical roles and drive operational continuity.',
    icon: Users,
    link: '/services/staffing',
    color: 'emerald',
    sections: [
      { label: 'For Organizations', href: '/services/staffing' },
      { label: 'For Job Seekers', href: '/services/staffing/job-seekers' },
    ],
  },
];

export default function ServicesPage() {
  usePageMeta({
    title: 'Services – Stratel',
    description: 'Explore Stratel’s full suite of healthcare services, including staffing strategy, compliance, training, and AI-driven insights.',
    keywords: 'healthcare services, staffing strategy, compliance consulting, training, AI insights',
  });
  return (
    <div>
      {/* Hero */}
      <section className="bg-gradient-to-br from-secondary-900 via-secondary-800 to-primary-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-display font-bold leading-tight">
              Our Services
            </h1>
            <p className="mt-6 text-xl text-secondary-300 leading-relaxed">
              Three comprehensive service areas designed to help long-term care organizations achieve operational excellence and sustainable growth.
            </p>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3 xl:gap-8">
            {mainServices.map((service) => {
              const Icon = service.icon;
              const colorClasses = {
                primary: 'from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800',
                blue: 'from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800',
                emerald: 'from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800',
              };
              
              return (
                <div
                  key={service.id}
                  className="group flex h-full flex-col overflow-hidden rounded-2xl border border-secondary-200 bg-white shadow-md transition-all duration-300 hover:shadow-xl"
                >
                  <div className={`bg-gradient-to-br ${colorClasses[service.color]} p-8 text-white`}>
                    <Icon className="w-12 h-12 mb-4" />
                    <h2 className="text-2xl font-bold">{service.title}</h2>
                  </div>
                  <div className="flex flex-1 flex-col p-6 sm:p-8">
                    <p className="mb-6 flex-1 leading-relaxed text-secondary-600">
                      {service.description}
                    </p>
                    <Link
                      to={service.link}
                      className="inline-flex items-center gap-2 text-secondary-900 font-semibold group-hover:gap-3 transition-all"
                    >
                      Learn More
                      <ArrowRight size={20} />
                    </Link>
                    {service.sections?.length ? (
                      <div className="mt-6 border-t border-secondary-100 pt-5">
                        <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">
                          Explore
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {service.sections.map((section) => (
                            <Link
                              key={section.href}
                              to={section.href}
                              className="max-w-full rounded-full border border-secondary-200 px-3 py-1.5 text-sm font-medium leading-tight text-secondary-700 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
                            >
                              {section.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Details */}
      <section className="py-20 bg-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-secondary-900">
              Why Stratel?
            </h2>
            <p className="mt-4 text-lg text-secondary-600">
              We understand the unique challenges facing long-term care organizations and provide practical support.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'LTC Experience',
                description: 'Specialized knowledge of long-term care operations, compliance, and staffing challenges.',
              },
              {
                title: 'Integrated Approach',
                description: 'Solutions across consulting, training, and staffing for comprehensive organizational support.',
              },
              {
                title: 'Outcome-Focused Support',
                description: 'Practical guidance designed to support efficiency, financial clarity, and staff retention.',
              },
            ].map((item) => (
              <div key={item.title} className="bg-white rounded-xl p-8 shadow-md">
                <h3 className="text-lg font-semibold text-secondary-900 mb-3">{item.title}</h3>
                <p className="text-secondary-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-display font-bold text-white">
            Ready to Get Started?
          </h2>
          <p className="mt-4 text-xl text-primary-100">
            Schedule a consultation with our team to discuss your organization's needs.
          </p>
          <Link
            to="/get-started"
            className="btn bg-white text-primary-600 hover:bg-primary-50 btn-lg mt-8 inline-flex"
          >
            Schedule a Consultation
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
}
